import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  darkOrange,
  lightAmber,
  lightRed,
  positiveGreen,
  positiveGreenBackground,
  red,
  squidInk,
} from 'lib/css/colors';

const STATUS = {
  VERIFIED: 'VERIFIED',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
  ERROR: 'ERROR',
};

function getStatusStyles(status) {
  switch (status) {
    case STATUS.VERIFIED:
      return {
        container: styles.containerVerified,
        accent: styles.accentVerified,
      };
    case STATUS.ERROR:
      return {
        container: styles.containerError,
        accent: styles.accentError,
      };
    case STATUS.NEEDS_REVIEW:
    default:
      return {
        container: styles.containerNeedsReview,
        accent: styles.accentNeedsReview,
      };
  }
}

function AISuggestionPanel({ title, status, confidence, children }) {
  const statusStyles = getStatusStyles(status);

  return (
    <div className={css(styles.container, statusStyles.container)}>
      <div className={css(styles.header)}>
        <div className={css(styles.titleGroup)}>
          <FontAwesomeIcon icon={faRobot} className={css(styles.icon, statusStyles.accent)} />
          <span className={css(styles.title)}>{title}</span>
        </div>
        <div className={css(styles.metaGroup)}>
          <span className={css(styles.confidence, statusStyles.accent)}>{confidence}%</span>
          <span className={css(styles.status, statusStyles.accent)}>{status}</span>
        </div>
      </div>
      <div className={css(styles.body)}>{children}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
    border: '1px solid transparent',
  },
  containerVerified: {
    backgroundColor: positiveGreenBackground,
    borderColor: positiveGreen,
  },
  containerNeedsReview: {
    backgroundColor: lightAmber,
    borderColor: darkOrange,
  },
  containerError: {
    backgroundColor: lightRed,
    borderColor: red,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: 8,
    fontSize: '0.95rem',
  },
  title: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: squidInk,
  },
  metaGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  confidence: {
    fontWeight: 700,
    fontSize: '0.85rem',
  },
  status: {
    fontWeight: 700,
    fontSize: '0.85rem',
    letterSpacing: 0.5,
  },
  accentVerified: {
    color: positiveGreen,
  },
  accentNeedsReview: {
    color: darkOrange,
  },
  accentError: {
    color: red,
  },
  body: {
    fontSize: '0.85rem',
    color: squidInk,
    lineHeight: 1.4,
  },
});

AISuggestionPanel.propTypes = {
  title: PropTypes.string.isRequired,
  status: PropTypes.oneOf([STATUS.VERIFIED, STATUS.NEEDS_REVIEW, STATUS.ERROR]).isRequired,
  confidence: PropTypes.number.isRequired,
  children: PropTypes.node,
};

export default AISuggestionPanel;
